<template>
    <div>
        <b-modal
            id="m-data-perubahan-temuan-inventory"
            :title="title || config.title"
            size="xl"
            no-close-on-backdrop
            no-close-on-esc
            hide-footer
            @show="resetForm"
        >
            <b-alert v-if="context === 'consignment'" show variant="info">
                Kode Titip Jual: <strong>{{ consignmentCode }}</strong>
                <div>Barang dari GA: <strong>{{ ga.item_type || "-" }}</strong> ({{ ga.item_kind || "-" }})</div>
                <small>Pilih jenis, merk, dan tipe yang sesuai dengan barang ini.</small>
            </b-alert>
            <div v-else class="mb-3">
                No. Faktur: <strong>{{ noFaktur || "-" }}</strong>
            </div>
            <b-form-group label-class="font-weight-bold">
                <template #label>
                    Kategori <span class="text-danger">*</span>
                </template>
                <multiselect
                    v-model="category"
                    label="name"
                    track-by="id"
                    :allow-empty="false"
                    :internal-search="true"
                    deselect-label=""
                    select-label=""
                    placeholder="Pilih kategori..."
                    :options="optCategory"
                />
            </b-form-group>
            <hr />
            <h6 class="font-weight-bold mb-3">Data Perubahan Barang</h6>
            <FormDataTemuan ref="form" :defaults="defaults" @kind-change="kindId = $event" @grade-change="grade = $event" />
            <hr />
            <template v-if="needsPhysical">
                <h6 class="font-weight-bold mb-3">
                    Foto Fisik <small v-if="!physicalRequired" class="text-muted font-weight-normal">(opsional untuk grade S/A)</small>
                </h6>
                <div v-if="!kindId" class="text-muted mb-3">Pilih jenis barang terlebih dahulu.</div>
                <b-row v-else>
                    <b-col v-for="slot in physicalPhotos" :key="slot.id" md="3" class="mb-3">
                        <div class="font-weight-bold mb-1">
                            {{ slot.name }} <span v-if="physicalRequired && slot.isRequired" class="text-danger">*</span>
                        </div>
                        <div v-if="slot.path" class="position-relative d-inline-block">
                            <MediaImage :src="slot.previewUrl" class="img-thumbnail media-thumb" />
                            <b-button size="sm" variant="danger" class="position-absolute media-remove" @click="clearPhysical(slot)">
                                &times;
                            </b-button>
                        </div>
                        <b-form-file
                            v-else
                            accept="image/*"
                            placeholder="Pilih foto..."
                            :disabled="isUploading"
                            @input="file => onPhysicalFile(slot, file)"
                        />
                    </b-col>
                </b-row>
                <hr />
            </template>
            <b-form-group label-class="font-weight-bold">
                <template #label>
                    Foto dan Video Temuan (Max 10) <span v-if="!physicalRequired" class="text-danger">*</span>
                </template>
                <b-form-file
                    v-model="tempFiles"
                    multiple
                    accept="image/*,video/*"
                    placeholder="Tambah foto dan video..."
                    drop-placeholder="Drop file disini..."
                    :file-name-formatter="() => 'Tambah foto dan video...'"
                    :disabled="isUploading || media.length >= 10"
                    @change="onFileChange"
                />
            </b-form-group>
            <div v-if="media.length" class="d-flex flex-wrap">
                <div v-for="(m, index) in media" :key="m.path" class="mr-2 mb-2 position-relative">
                    <video
                        v-if="m.type === 'video'"
                        :src="m.previewUrl"
                        class="img-thumbnail media-thumb"
                        preload="metadata"
                    ></video>
                    <MediaImage v-else :src="m.previewUrl" class="img-thumbnail media-thumb" />
                    <b-button
                        size="sm"
                        variant="danger"
                        class="position-absolute media-remove"
                        @click="media.splice(index, 1)"
                    >
                        &times;
                    </b-button>
                </div>
            </div>
            <b-form-group label-class="font-weight-bold" class="mt-3">
                <template #label>
                    Catatan
                </template>
                <b-form-textarea v-model="notes" rows="3" maxlength="255" placeholder="Masukkan catatan" />
                <small class="text-muted">{{ notes.length }} / 255</small>
            </b-form-group>
            <div class="text-right">
                <b-button variant="secondary" class="mr-2" @click="$bvModal.hide('m-data-perubahan-temuan-inventory')">
                    Batal
                </b-button>
                <b-button variant="primary" :disabled="isSubmitting || isUploading" @click="submit">
                    {{ isSubmitting ? "Menyimpan..." : "Simpan" }}
                </b-button>
            </div>
            <b-overlay :show="isUploading" no-wrap />
        </b-modal>
    </div>
</template>

<script>
import FormDataTemuan from "./FormDataTemuan.vue";
import MediaImage from "./MediaImage.vue";
import { GRADES_WITH_PHOTO, splitMedia, uploadMedia } from "@/shared/inventory-variance";

const UPLOAD_DIR = "inventory-variances";
const PHYSICAL_UPLOAD_DIR = "invtrypht";

const CONTEXTS = {
    create: {
        title: "Buat Temuan",
        confirm: "Buat temuan untuk barang ini?",
        success: "Temuan berhasil dibuat"
    },
    reset: {
        title: "Request Reset Inventory",
        confirm: "Request reset inventory ini kembali ke PENDING?",
        success: "Request berhasil dikirimkan!"
    },
    proposal: {
        title: "Isi Data Perubahan Temuan",
        confirm: "Simpan data perubahan temuan ini?",
        success: "Data perubahan berhasil disimpan"
    },
    consignment: {
        title: "Buat Temuan Titip Jual",
        confirm: "Buat temuan untuk barang titip jual ini?",
        success: "Temuan berhasil dibuat"
    }
};

export default {
    name: "ModalDataPerubahan",
    components: { FormDataTemuan, MediaImage },
    props: {
        context: {
            type: String,
            required: true,
            validator: value => Object.keys(CONTEXTS).includes(value)
        },
        inventoryId: { type: Number, default: null },
        varianceId: { type: Number, default: null },
        noFaktur: { type: String, default: "" },
        defaults: { type: Object, default: null },
        title: { type: String, default: "" },
        // A revision starts from the stored proposal: its category and media (mapped sm_media).
        defaultCategory: { type: String, default: "" },
        defaultMedia: { type: Array, default: null },
        consignmentCode: { type: String, default: "" },
        batchId: { type: Number, default: null }
    },
    data() {
        return {
            category: null,
            notes: "",
            media: [],
            tempFiles: [],
            optCategory: [],
            isUploading: false,
            isSubmitting: false,
            kindId: null,
            grade: null,
            physicalPhotos: [],
            // Stored physical photos to prefill; one the user clears is dropped so it does not come back on reload.
            storedPhysical: []
        };
    },
    computed: {
        config() {
            return CONTEXTS[this.context];
        },
        // A consignment item has no NF yet, so its media is keyed by the consignment code.
        referenceKey() {
            return this.context === "consignment" ? this.consignmentCode : this.noFaktur;
        },
        ga() {
            return this.defaults || {};
        },
        needsPhysical() {
            return !!(this.category && this.category.requires_physical_photos);
        },
        physicalRequired() {
            return this.needsPhysical && GRADES_WITH_PHOTO.includes(this.grade);
        },
        defaultSplitMedia() {
            return splitMedia(this.defaultMedia);
        }
    },
    watch: {
        needsPhysical: "loadPhysicalCategories",
        kindId: "loadPhysicalCategories"
    },
    methods: {
        resetForm() {
            this.category = null;
            this.notes = "";
            // Stored media is resubmitted by path, so it needs no re-upload.
            this.media = this.defaultSplitMedia.evidence
                .filter(m => m.path)
                .map(m => ({ path: m.path, type: m.type, previewUrl: m.url }));
            this.tempFiles = [];
            this.physicalPhotos = [];
            this.storedPhysical = this.defaultSplitMedia.physical.filter(p => p.path);
            this.kindId = null;
            this.grade = null;
            const categories = this.optCategory.length
                ? Promise.resolve(this.optCategory)
                : this.$axios.get("master/inventory-variance-categories").then(res => (this.optCategory = res.data.data));
            categories
                .then(options => (this.category = options.find(c => c.name === this.defaultCategory) || null))
                .catch(error => this.$helper.parseError(this, error));
        },

        uploadFile(file, dir = UPLOAD_DIR) {
            return uploadMedia(this.$axios, file, dir, this.referenceKey);
        },

        // Same master as barang-masuk upload, photos only.
        loadPhysicalCategories() {
            if (!this.needsPhysical || !this.kindId) {
                this.physicalPhotos = [];
                return;
            }
            const kindId = this.kindId;
            this.$axios
                .get(`master/kategori-foto/selection?kind_id=${kindId}`)
                .then(res => {
                    // A later kind change already started its own load.
                    if (kindId !== this.kindId || !this.needsPhysical) return;
                    this.physicalPhotos = res.data.data
                        .filter(c => c.is_video != 1)
                        .map(c => {
                            const photo = this.storedPhysical.find(p => p.category_id == c.id);
                            return {
                                id: c.id,
                                name: c.name,
                                isRequired: c.is_required == 1,
                                path: photo ? photo.path : null,
                                previewUrl: photo ? photo.url : null
                            };
                        });
                })
                .catch(error => this.$helper.parseError(this, error));
        },

        clearPhysical(slot) {
            this.storedPhysical = this.storedPhysical.filter(p => p.category_id != slot.id);
            slot.path = slot.previewUrl = null;
        },

        async onPhysicalFile(slot, file) {
            if (!file) return;
            this.isUploading = true;
            try {
                const { path, previewUrl } = await this.uploadFile(file, PHYSICAL_UPLOAD_DIR);
                slot.path = path;
                slot.previewUrl = previewUrl;
            } catch (error) {
                this.$helper.toastError(this, "Gagal mengupload foto fisik");
            } finally {
                this.isUploading = false;
            }
        },

        async onFileChange(e) {
            const files = e && e.target && e.target.files ? Array.from(e.target.files) : [];
            if (!files.length) return;

            if (this.media.length + files.length > 10) {
                this.$nextTick(() => (this.tempFiles = []));
                return this.$helper.toastError(this, "Maksimal 10 foto/video");
            }

            this.isUploading = true;
            try {
                const uploaded = await Promise.all(files.map(file => this.uploadFile(file)));
                this.media.push(...uploaded);
            } catch (error) {
                this.$helper.toastError(this, "Gagal mengupload foto/video");
            } finally {
                this.isUploading = false;
                this.$nextTick(() => (this.tempFiles = []));
            }
        },

        async submit() {
            if (!this.category) return this.$helper.toastError(this, "Kategori wajib dipilih");
            const formError = this.$refs.form.validate();
            if (formError) return this.$helper.toastError(this, formError);
            if (this.physicalRequired) {
                const missing = this.physicalPhotos.filter(s => s.isRequired && !s.path).map(s => s.name);
                if (missing.length) return this.$helper.toastError(this, `Foto fisik wajib diisi: ${missing.join(", ")}`);
            } else if (!this.media.length) {
                return this.$helper.toastError(this, "Foto dan video temuan wajib diupload");
            }
            if (!confirm(this.config.confirm)) return;

            const payload = {
                category: this.category.name,
                notes: this.notes.trim() || null,
                media: this.media.map(m => m.path),
                physical_photos: this.physicalPhotos.filter(s => s.path).map(s => ({ category_id: s.id, path: s.path })),
                data: this.$refs.form.getData()
            };
            const request = {
                create: ["inventory-variances", { inventory_id: this.inventoryId, ...payload }],
                reset: ["inventory/request-reset", { id: this.inventoryId, ...payload }],
                proposal: [`inventory-variances/${this.varianceId}/proposal`, payload],
                consignment: [
                    "inventory-variances/consignment",
                    { consignment_code: this.consignmentCode, batch_id: this.batchId, ...payload }
                ]
            }[this.context];

            this.isSubmitting = true;
            try {
                const res = await this.$axios.post(request[0], request[1]);
                this.$helper.toastSuccess(this, this.config.success);
                this.$bvModal.hide("m-data-perubahan-temuan-inventory");
                this.$emit("saved", res.data.data);
            } catch (error) {
                this.$helper.parseError(this, error);
            } finally {
                this.isSubmitting = false;
            }
        }
    }
};
</script>

<style scoped>
.media-thumb {
    width: 100px;
    height: 100px;
    object-fit: cover;
}

.media-remove {
    top: 0;
    right: 0;
    padding: 0 4px;
}
</style>
